import React, { useState } from 'react';
import { MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useEscortsData } from '../hooks/useEscortsData';
import { useTranslation } from '../hooks/useTranslation';
import useMobile from '../hooks/useMobile';

const MOBILE_INITIAL_COUNT = 4; // pill mostrate su mobile prima di espandere

interface CityData {
  name: string;
  count: number;
}

interface CitySelectorProps {
  onCitySelect: (location: string) => void;
}

const CitySelector: React.FC<CitySelectorProps> = ({ onCitySelect }) => {
  const { escorts } = useEscortsData();
  const [showAllCities, setShowAllCities] = useState(false);
  const { t } = useTranslation();
  const isMobile = useMobile();

  // Funzione per estrarre e contare le città dai dati API
  const getCitiesFromAPI = (): CityData[] => {
    const cityCount = new Map<string, number>();
    
    escorts.forEach(escort => {
      if (escort.location) {
        // Normalizza il nome della città (rimuovi spazi extra, capitalizza)
        const cityName = escort.location.trim()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        
        cityCount.set(cityName, (cityCount.get(cityName) || 0) + 1);
      }
    });
    
    // Converti in array e ordina per numero di annunci (decrescente)
    return Array.from(cityCount.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  };

  // Città con più annunci (threshold: almeno 1 annuncio)
  const getTopCities = (): CityData[] => {
    return getCitiesFromAPI().filter(city => city.count >= 1);
  };

  // Città da mostrare in base allo stato
  const getCitiesToShow = (): CityData[] => {
    const topCities = getTopCities();
    if (showAllCities) return topCities;
    if (isMobile) return topCities.slice(0, MOBILE_INITIAL_COUNT);
    return topCities.slice(0, 12);
  };

  const hasMore = showAllCities
    ? false
    : isMobile
    ? getTopCities().length > MOBILE_INITIAL_COUNT
    : getTopCities().length > 12;

  const handleCitySelect = (cityName: string) => {
    onCitySelect(cityName);
  };

  return (
    <>
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className={`text-3xl font-bold text-gray-800 text-center ${isMobile ? 'hidden' : ''}`}>
            {t('citySelector.title')}
          </h2>
          <p className={`text-gray-600 text-center mb-8 max-w-2xl mx-auto ${isMobile ? 'hidden' : ''}`}>
            {t('citySelector.subtitle')}
          </p>
          
          <div className="flex flex-wrap gap-2">
            {getCitiesToShow().map((city) => (
              <button
                key={city.name}
                onClick={() => handleCitySelect(city.name)}
                className="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 shadow-sm hover:shadow-md transition-all duration-200 group hover:bg-pink-50 border border-gray-200 hover:border-pink-300"
                style={{ width: '160px' }}
                title={city.name}
                aria-label={`${city.name} - ${city.count} ${city.count === 1 ? t('citySelector.singleAd') : t('citySelector.multipleAds')}`}
              >
                <MapPin className="h-3.5 w-3.5 text-pink-500 flex-shrink-0 group-hover:text-pink-600" />
                <span className="text-sm font-medium text-gray-800 group-hover:text-pink-600 truncate flex-1 text-left">
                  {city.name}
                </span>
                <span className="bg-pink-100 text-pink-700 text-xs px-1.5 py-0.5 rounded-full group-hover:bg-pink-200 flex-shrink-0">
                  {city.count}
                </span>
              </button>
            ))}
          </div>
          
          {/* Bottone per mostrare/nascondere tutte le città */}
          {(hasMore || showAllCities) && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAllCities(!showAllCities)}
                className={`inline-flex items-center bg-white border border-gray-300 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 text-gray-700 hover:text-pink-600 hover:border-pink-300 ${isMobile ? 'px-3 py-1.5 text-sm' : 'px-6 py-3'}`}
                aria-label={showAllCities ? t('citySelector.showMainCities') : t('citySelector.showAllCities')}
              >
                {showAllCities ? (
                  <>
                    <ChevronUp className="h-5 w-5 mr-2" />
                    {t('citySelector.showMainCities')}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-5 w-5 mr-2" />
                    {t('citySelector.showAllCities')} ({getTopCities().length})
                  </>
                )}
              </button>
            </div>
          )}
          
          {getCitiesToShow().length === 0 && (
            <div className="text-center py-8">
              <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">{t('citySelector.noCitiesAvailable')}</p>
              <p className="text-gray-400 text-sm mt-2">{t('citySelector.dataLoadingMessage')}</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CitySelector;
